import {useState, useCallback} from "react";
import {CourtBigModel, CourtCreateRequestModel, CourtsRequests, CourtTypeEnum} from "@/app/api/courts";
import usePhotoUpload from "@/app/utils/use_photo_upload";

export interface ScreenState {
  hasError?: string;
  showSelectCourtPlace?: boolean;
  pointSelected?: {
    lat: number;
    lon: number;
  } | undefined
  showCreateCourt?: boolean;
  court: CourtBigModel
  images?: string[]
  cancel?: boolean
  wantToLeave: boolean
  courtCreated: boolean
  loading: boolean
}

const useCreateCourt = () => {
  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    showSelectCourtPlace: false, pointSelected: undefined,
    showCreateCourt: false,
    court: {
      id: 0,
      name: "",
      address: "",
      bookingRequired: false,
      artificialLight: false,
      lockerRooms: false,
      parkingAvailability: false,
      coachAvailable: false,
      hasCafeOrBar: false,
      rentalGearAvailable: false,
      socialVibe: false,
      description: "",
      describeThisLocation: "",
      imagesUrls: [],
      latitude: 0,
      longitude: 0,
      type: [CourtTypeEnum.OUTDOOR],
      matchesPlayed: 0,
      numberOfCourts: 0,
      avgRating: 0,
      websiteLink: ""
    },
    images: [],
    cancel: false,
    wantToLeave: false,
    courtCreated: false,
    loading: false
  });

  const {pickPhoto, uploadBase64} = usePhotoUpload();

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  const selectPoint = useCallback((coords: { lat: number; lon: number } | undefined) => {
    updateState({pointSelected: coords});
  }, [updateState]);

  const setShowCreateCourt = useCallback(() => {
    if (!state.pointSelected) return;
    updateState({showCreateCourt: true});
  }, [state.showCreateCourt, updateState]);

  const setType = useCallback((type: CourtTypeEnum) => {
    if (state.court.type.includes(type)) {
      updateState({court: {...state.court, type: state.court.type.filter(t => t !== type)}});
      return;
    } else {
      updateState({court: {...state.court, type: [...state.court.type, type]}});
      return;
    }
  }, [state.court, updateState]);

  const setPlaceCount = useCallback((count: number) => {
    if (state.court.numberOfCourts === count) return;
    updateState({court: {...state.court, numberOfCourts: count}});
  }, [state.court, updateState]);

  const setRating = useCallback((rating: number) => {
    if (state.court.avgRating === rating) return;
    updateState({court: {...state.court, avgRating: rating}});
  }, [state.court, updateState]);

  const setTitle = useCallback((title: string) => {
    if (state.court.name === title) return;
    updateState({court: {...state.court, name: title}});
  }, [state.court, updateState]);

  const setDescription = useCallback((description: string) => {
    if (state.court.description === description) return;
    updateState({court: {...state.court, description}});
  }, [state.court, updateState]);

  const setArtificialLight = useCallback((artificialLight: boolean) => {
    if (state.court.artificialLight === artificialLight) return;
    updateState({court: {...state.court, artificialLight}});
  }, [state.court, updateState]);

  const setLockerRooms = useCallback((lockerRooms: boolean) => {
    if (state.court.lockerRooms === lockerRooms) return;
    updateState({court: {...state.court, lockerRooms}});
  }, [state.court, updateState]);

  const setParkingAvailability = useCallback((parkingAvailability: boolean) => {
    if (state.court.parkingAvailability === parkingAvailability) return;
    updateState({court: {...state.court, parkingAvailability}});
  }, [state.court, updateState]);

  const setBookingRequired = useCallback((bookingRequired: boolean) => {
    if (state.court.bookingRequired === bookingRequired) return;
    updateState({court: {...state.court, bookingRequired}});
  }, [state.court, updateState]);

  const setDescribeThisLocation = useCallback((describeThisLocation: string) => {
    if (state.court.describeThisLocation === describeThisLocation) return;
    updateState({court: {...state.court, describeThisLocation}});
  }, [state.court, updateState]);

  const setCoachAvailable = useCallback((coachAvailable: boolean) => {
    if (state.court.coachAvailable === coachAvailable) return;
    updateState({court: {...state.court, coachAvailable}});
  }, [state.court, updateState]);

  const setHasCafeOrBar = useCallback((hasCafeOrBar: boolean) => {
    if (state.court.hasCafeOrBar === hasCafeOrBar) return;
    updateState({court: {...state.court, hasCafeOrBar}});
  }, [state.court, updateState]);

  const setRentalGearAvailable = useCallback((rentalGearAvailable: boolean) => {
    if (state.court.rentalGearAvailable === rentalGearAvailable) return;
    updateState({court: {...state.court, rentalGearAvailable}});
  }, [state.court, updateState]);

  const setSocialVibe = useCallback((socialVibe: boolean) => {
    if (state.court.socialVibe === socialVibe) return;
    updateState({court: {...state.court, socialVibe}});
  }, [state.court, updateState]);

  const setAddress = useCallback((address: string) => {
    if (state.court.address === address) return;
    updateState({court: {...state.court, address}});
  }, [state.court, updateState]);

  const setWebsite = useCallback((website: string) => {
    if (state.court.websiteLink === website) return;
    updateState({court: {...state.court, websiteLink: website}});
  }, [state.court, updateState]);

  const selectPhoto = useCallback(async (index: number) => {
    const images = state.images || [];
    const base64 = await pickPhoto();
    if (base64 === undefined) return;
    if (base64) {
      images[index] = base64 ?? "";
      updateState({images: images});
    }
  }, [state.showCreateCourt, updateState]);

  const unselectPhoto = useCallback((index: number) => {
    const images = state.images || [];
    images[index] = "";
    updateState({images: images});
  }, [state.showCreateCourt, updateState]);

  const cancel = useCallback(() => {
    updateState({wantToLeave: true});
  }, [state.showCreateCourt, updateState]);

  const addCourtToMap = useCallback(async (coords: { lat: number; lon: number } | undefined) => {
    updateState({loading: true});
    if (!coords) return;
    const data: CourtCreateRequestModel = {
      ...state.court,
      imagesUrls: [],
      latitude: coords?.lat,
      longitude: coords?.lon
    };

    if (state.images) {
      data.imagesUrls = (await Promise.all(
        state.images.map(async (image) => image ? await uploadBase64(image) : null)
      )).filter((v): v is string => typeof v === 'string');
    }

    await CourtsRequests.createCourt(data);
    updateState({courtCreated: true, loading: false});
  }, [state.court, state.images, updateState]);

  const goBack = useCallback(() => {
    updateState({wantToLeave: false});
  }, [state.showCreateCourt, updateState]);

  const setPrivateType = useCallback((privateType: string) => {
    if (state.court.describeThisLocation === privateType) return;
    updateState({court: {...state.court, describeThisLocation: privateType}});
  }, [state.court, updateState]);

  const calculateDisabled = useCallback(() => {
    return !state.court.name || !state.court.description || !state.court.address || !state.court.websiteLink || !state.court.type || !state.court.describeThisLocation || !state.court.numberOfCourts || (state?.images?.length === 0);
  }, [state.court]);

  return {
    state,
    selectPoint, setShowCreateCourt,
    setType,
    setPlaceCount, setRating,
    setTitle, setDescription,
    setArtificialLight,
    setLockerRooms,
    setParkingAvailability,
    setBookingRequired,
    selectPhoto,
    unselectPhoto,
    cancel,
    goBack,
    addCourtToMap,
    setAddress,
    setCoachAvailable,
    setSocialVibe,
    setHasCafeOrBar,
    setRentalGearAvailable,
    setPrivateType,
    setWebsite,
    calculateDisabled
  };
};

export default useCreateCourt;